import { useCallback, useRef } from 'react';
import {
  UseAlreadyPurchasedDiningReviseParams,
  UseAlreadyPurchasedDiningReviseResult,
} from '@msc-b2c/interfaces/interfaces';
import { DiningSelectionMetadata } from '@msc-b2c/interfaces/types';
import { ReviseThematicReservationsMyMSC } from '@msc-b2c/shared-utils';
import { BookingStatusEnum } from '@msc-b2c/interfaces/enums';

/**
 * Extracts unique selected passenger IDs from the drawer passenger matrix.
 *
 * Input shape:
 * - cabinId -> passengerId -> isSelected
 */
const extractSelectedPassengerIds = (selection: Record<string, Record<string, boolean>>) => {
  return Array.from(
    new Set(
      Object.values(selection).flatMap((cabinPassengers) =>
        Object.entries(cabinPassengers)
          .filter(([, isSelected]) => isSelected)
          .map(([passengerId]) => passengerId)
      )
    )
  );
};

/**
 * Coordinates INT163 revise payload creation for ALREADY_PURCHASED dining edits.
 *
 * Flow responsibilities:
 * - capture latest drawer selection/pax state,
 * - map edited reservations to existing thematicBookingId when possible,
 * - build revise payload and trigger API call.
 */
export const useAlreadyPurchasedDiningRevise = ({
  booking,
  bookingNumber,
  cruiseId,
  onboardServiceCode,
}: UseAlreadyPurchasedDiningReviseParams): UseAlreadyPurchasedDiningReviseResult => {
  /**
   * Pending drawer selection snapshot.
   * Refs are used so parent flow can cache then apply later without forcing re-renders.
   */
  const pendingDiningSelectionRef = useRef<DiningSelectionMetadata | undefined>(undefined);
  const pendingDiningPassengerIdsRef = useRef<string[]>([]);

  /**
   * Clears pending selection state after apply/abort.
   */
  const resetPendingDiningSelection = useCallback(() => {
    pendingDiningSelectionRef.current = undefined;
    pendingDiningPassengerIdsRef.current = [];
  }, []);

  /**
   * Stores latest drawer passenger + reservation snapshot for deferred apply.
   */
  const cacheDiningSelectionFromDrawer = useCallback(
    (
      passengers: Record<string, Record<string, boolean>>,
      diningSelection?: DiningSelectionMetadata
    ) => {
      pendingDiningSelectionRef.current = diningSelection;
      pendingDiningPassengerIdsRef.current = extractSelectedPassengerIds(passengers);
    },
    []
  );

  /**
   * Builds and sends revise payload using currently cached selection.
   *
   * Matching strategy for thematicBookingId:
   * 1. same passenger + restaurant + date + slot -> preserve id (unchanged)
   * 2. same passenger + restaurant + date (slot changed) -> preserve id (time edit)
   * 3. no match -> omit id (new reservation)
   */
  const applyDiningSelectionRevise = useCallback(async () => {
    const capturedDiningSelection = pendingDiningSelectionRef.current;
    const capturedPassengerIds = [...pendingDiningPassengerIdsRef.current];
    resetPendingDiningSelection();

    if (!capturedDiningSelection?.reservations?.length || !onboardServiceCode) {
      return;
    }

    const bookedPrepaidForCruise =
      booking?.prepaid?.bookings?.find(
        (prepaidBooking) =>
          prepaidBooking.cruise?.id === cruiseId &&
          (prepaidBooking.onboardServices ?? []).some(
            (service) =>
              service.code === onboardServiceCode &&
              service.bookingStatus.code === BookingStatusEnum.BOOKED
          )
      ) ?? null;
    const prepaidBookingNumber = bookedPrepaidForCruise?.number;

    if (!bookingNumber || !prepaidBookingNumber) {
      console.error('INT163 skipped: missing bookingNumber or prepaidBookingNumber', {
        bookingNumber,
        prepaidBookingNumber,
      });
      return;
    }

    /**
     * When drawer has no explicit selected passengers, fallback to passengers
     * already linked to this onboard service in prepaid data.
     */
    const fallbackPassengerIds = Array.from(
      new Set(
        (bookedPrepaidForCruise?.onboardServices ?? [])
          .filter((service) => service.code === onboardServiceCode)
          .map((service) => service.passenger?.id)
          .filter((id): id is string => Boolean(id))
      )
    );
    const passengerIds =
      capturedPassengerIds.length > 0 ? capturedPassengerIds : fallbackPassengerIds;

    if (passengerIds.length === 0) {
      console.error('INT163 skipped: no passenger IDs available for revise request');
      return;
    }

    const existingBookingsByPassengerRestaurant = new Map<
      string,
      Array<{ date: string; slot: string; thematicBookingId: string }>
    >();
    (bookedPrepaidForCruise?.onboardServices ?? [])
      .filter((service) => service.code === onboardServiceCode)
      .forEach((service) => {
        const passengerId = service.passenger?.id;
        if (!passengerId) {
          return;
        }

        (service.diningRooms ?? []).forEach((diningRoom) => {
          const restaurantCode = diningRoom?.code;
          const date = diningRoom?.date;
          const slot = diningRoom?.slot;
          const thematicBookingId = diningRoom?.thematicBookingId;

          if (restaurantCode && date && slot && thematicBookingId) {
            const key = `${passengerId}|${restaurantCode}`;
            const existing = existingBookingsByPassengerRestaurant.get(key) ?? [];
            existing.push({ date, slot, thematicBookingId });
            existingBookingsByPassengerRestaurant.set(key, existing);
          }
        });
      });

    const revisePayload = {
      diningRooms: capturedDiningSelection.reservations
        .filter((reservation) => reservation.day && reservation.hour && reservation.restaurantCode)
        .map((reservation) => ({
          code: reservation.restaurantCode,
          date: reservation.day,
          time: reservation.hour,
          passengers: passengerIds.map((id) => {
            const key = `${id}|${reservation.restaurantCode}`;
            const existingBookings = existingBookingsByPassengerRestaurant.get(key) ?? [];

            let thematicBookingId: string | undefined;

            const exactMatch = existingBookings.find(
              (bookingMatch) =>
                bookingMatch.date === reservation.day && bookingMatch.slot === reservation.hour
            );
            if (exactMatch) {
              thematicBookingId = exactMatch.thematicBookingId;
            } else {
              const dateMatchWithDifferentTime = existingBookings.find(
                (bookingMatch) =>
                  bookingMatch.date === reservation.day && bookingMatch.slot !== reservation.hour
              );
              if (dateMatchWithDifferentTime) {
                thematicBookingId = dateMatchWithDifferentTime.thematicBookingId;
              }
            }

            return {
              id,
              ...(thematicBookingId ? { thematicBookingId } : {}),
            };
          }),
        })),
    };

    if (revisePayload.diningRooms.length === 0) {
      return;
    }

    const reviseApi = new ReviseThematicReservationsMyMSC(false, false, revisePayload, {
      bookingNumber,
      prepaidBookingNumber,
      onboardServiceCode,
    });
    await reviseApi.sendData();
  }, [booking, bookingNumber, cruiseId, onboardServiceCode, resetPendingDiningSelection]);

  /**
   * Convenience method for immediate cache + apply in one call.
   */
  const applyDiningSelectionReviseDirect = useCallback(
    async (
      passengers: Record<string, Record<string, boolean>>,
      diningSelection?: DiningSelectionMetadata
    ) => {
      cacheDiningSelectionFromDrawer(passengers, diningSelection);
      await applyDiningSelectionRevise();
    },
    [applyDiningSelectionRevise, cacheDiningSelectionFromDrawer]
  );

  return {
    cacheDiningSelectionFromDrawer,
    resetPendingDiningSelection,
    applyDiningSelectionRevise,
    applyDiningSelectionReviseDirect,
  };
};
